import { useState, useCallback, useRef } from 'react';
import type { Tile } from '../types/dualGame';
import {
  resetTileIds,
  createEmptyBoard,
  addRandomTile,
  moveBoard,
  canMove,
  hasReachedValue,
  type Direction,
} from '../utils/gameLogic';

export type SingleStatus = 'playing' | 'won' | 'over';

export interface SingleGameState {
  board: number[][];
  tiles: Tile[];
  score: number;
  bestScore: number;
  status: SingleStatus;
  keepPlaying: boolean;
}

export type SingleEvent =
  | { type: 'move'; scoreDelta: number; merged: boolean }
  | { type: 'win' }
  | { type: 'over' };

export type SingleEventCallback = (event: SingleEvent) => void;

const BEST_SCORE_KEY = '2048_best_score';

const loadBestScore = (): number => {
  try { return parseInt(localStorage.getItem(BEST_SCORE_KEY) || '0', 10) || 0; }
  catch { return 0; }
};

const saveBestScore = (score: number) => {
  try { localStorage.setItem(BEST_SCORE_KEY, String(score)); }
  catch { /* storage unavailable */ }
};

const createInitial = (): SingleGameState => {
  resetTileIds();
  let board = createEmptyBoard(4);
  const r1 = addRandomTile(board); board = r1.board;
  const r2 = addRandomTile(board); board = r2.board;
  const tiles = [r1.tile, r2.tile].filter(Boolean) as Tile[];

  return {
    board,
    tiles,
    score: 0,
    bestScore: loadBestScore(),
    status: 'playing',
    keepPlaying: false,
  };
};

export const useSingleGame = (onEvent?: SingleEventCallback) => {
  const [state, setState] = useState<SingleGameState>(createInitial);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const newGame = useCallback(() => {
    setState(createInitial());
  }, []);

  const continueAfterWin = useCallback(() => {
    setState(prev => ({ ...prev, status: 'playing', keepPlaying: true }));
  }, []);

  const move = useCallback((direction: Direction) => {
    setState(prev => {
      if (prev.status === 'over' || prev.status === 'won') return prev;

      const { newBoard, newTiles, scoreDelta, hasMoved, hasMerge } = moveBoard(prev.board, prev.tiles, direction);
      if (!hasMoved) return prev;

      const spawnResult = addRandomTile(newBoard);
      const finalBoard = spawnResult.board;
      const finalTiles = spawnResult.tile ? [...newTiles, spawnResult.tile] : newTiles;

      const newScore = prev.score + scoreDelta;
      const best = Math.max(prev.bestScore, newScore);
      if (best > prev.bestScore) saveBestScore(best);

      onEventRef.current?.({ type: 'move', scoreDelta, merged: hasMerge });

      const isDead = !canMove(finalBoard);

      if (!prev.keepPlaying && hasReachedValue(finalTiles, 2048)) {
        onEventRef.current?.({ type: 'win' });
        return { ...prev, board: finalBoard, tiles: finalTiles, score: newScore, bestScore: best, status: 'won' as const };
      }

      if (isDead) {
        onEventRef.current?.({ type: 'over' });
        return { ...prev, board: finalBoard, tiles: finalTiles, score: newScore, bestScore: best, status: 'over' as const };
      }

      return { ...prev, board: finalBoard, tiles: finalTiles, score: newScore, bestScore: best };
    });
  }, []);

  return {
    ...state,
    newGame,
    continueAfterWin,
    move,
  };
};

export default useSingleGame;
